import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom'; // Import navigation hook
import { FaBed } from 'react-icons/fa'; // Import Bed icons
import { IoBedOutline } from 'react-icons/io5'; // Import empty bed icon
import { apiPost, apiGet } from '../../api/apiClient'; // Import API client
import { toastSuccess, toastError, toastConfirm } from '../common/toast'; // Import toast utilities
import RTButton from '../common/RTButton'; // Import RTButton component
import RTSpinner from '../common/RTSpinner'; // Import RTSpinner for loading state

import './Beds.css'; // Import custom CSS for styling

const Beds = ({ room, roomNumber, buildingName, roomName }) => {
  const { room_id } = room; // Destructure room_id
  const [selectedBed, setSelectedBed] = useState(null);
  const [selectedBedNumber, setSelectedBedNumber] = useState(null); // State to track the selected bed number
  const [bedList, setBedList] = useState([]); // State to manage the latest bed list
  const [loading, setLoading] = useState(true); // Loading state for beds component
  const navigate = useNavigate(); // Use navigate hook for navigation

  // Function to fetch beds and update bedList
  const fetchBeds = useCallback(async () => {
    setLoading(true);
    try {
      const updatedBeds = await apiGet(`/beds/${room_id}`); // Fetch latest beds
      setBedList(updatedBeds.data); // Update bed list
    } catch (error) {
      toastError('Failed to fetch beds.');
    } finally {
      setLoading(false);
    }
  }, [room_id]);

  useEffect(() => {
    fetchBeds(); // Fetch beds when component mounts
    setSelectedBed(null); // Reset selected bed when room changes
    setSelectedBedNumber(null);
  }, [fetchBeds]);

  // Handle card click to select a bed
  const handleCardClick = useCallback((bed, bedNumber) => {
    setSelectedBed(bed); // Set the selected bed ID
    setSelectedBedNumber(bedNumber); // Set the selected bed number
  }, []);

  // Memoize bed cards generation to avoid unnecessary re-renders
  const bedCards = useMemo(() => {
    return bedList.map((bed, index) => {
      let cardColor, textColor;
      if (bed.is_future_allocation) {
        cardColor = '#d6eaf8'; // Lighter blue for future allocation
        textColor = '#1a5276';
      } else if (bed.end_date) {
        cardColor = '#fff3cd'; // Amber for beds with end_date
        textColor = '#856404';
      } else if (bed.is_vacant) {
        cardColor = '#d4edda'; // Green for vacant
        textColor = '#155724';
      } else {
        cardColor = '#f8d7da'; // Red for occupied
        textColor = '#721c24';
      }
      const BedIcon = bed.is_vacant ? IoBedOutline : FaBed;

      const formatDate = (dateStr) => {
        if (!dateStr) return null;
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      };

      return (
        <div
          key={bed.bed_id}
          className="bed-card"
          style={{
            backgroundColor: cardColor,
            color: textColor,
            border: selectedBed && bed.bed_id === selectedBed.bed_id ? '2px solid #153C7F' : '1px solid #ddd',
            borderRadius: '8px',
            cursor: 'pointer',
            marginRight: '50px',
            marginLeft: '50px',
            transition: 'border 0.1s ease',
          }}
          onClick={() => handleCardClick(bed, index + 1)}
        >
          <div className="bed-type">Bed #{index + 1}</div>
          <div className="bed-type">{bed.pg_name}</div>
          <BedIcon className="big-icon" />
          {(bed.is_future_allocation || bed.end_date) && (
            <div style={{ fontSize: '11px', marginTop: '4px', textAlign: 'center', lineHeight: '1.4' }}>
              {bed.is_future_allocation && bed.start_date && <div>Starting on: {formatDate(bed.start_date)}</div>}
              {bed.end_date && <div>Ending on: {formatDate(bed.end_date)}</div>}
            </div>
          )}
        </div>
      );
    });
  }, [bedList, selectedBed, handleCardClick]);

  // Memoize rows calculation
  const rows = useMemo(() => {
    const rowsArray = [];
    for (let i = 0; i < bedCards.length; i += 3) {
      rowsArray.push(bedCards.slice(i, i + 3));
    }
    return rowsArray;
  }, [bedCards]);

  // Handle closing the menu when clicking outside
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.bed-card') && !e.target.closest('.action-menu-row')) {
        setSelectedBed(null); // Deselect the bed
      }
    };

    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, []);

  // Handle Add Bed functionality
  const handleAddBed = async () => {
    try {
      await apiPost('/beds', { room_id }); // Send room_id in the POST request
      toastSuccess('Bed added successfully!');
      await fetchBeds(); // Fetch the latest beds
    } catch (error) {
      toastError(error.response?.data?.detail || 'Failed to add bed.');
    }
  };

  // Handle Delete Bed functionality
  const handleDeleteBed = async () => {
    if (!selectedBed) return;

    // Check if the bed has a pg_id
    if (selectedBed.pg_id) {
      toastError('Move the PG first to delete the bed.');
      return;
    }

    // Show confirmation dialog
    const confirmed = await toastConfirm(
        true,
      `Are you sure you want to delete Bed #${selectedBedNumber}?`,
    );
    if (!confirmed) return;

    try {
      await apiPost(`/beds/${selectedBed.bed_id}/delete`); // Call delete API
      toastSuccess('Bed deleted successfully!');
      await fetchBeds(); // Fetch the latest beds
      setSelectedBed(null); // Deselect the bed after deletion
    } catch (error) {
      toastError(error.response?.data?.detail || 'Failed to delete bed.');
    }
  };

  // Handle menu button click
  const handleMenuButtonClick = (action) => {
    if (action === 'Add Bed') {
      handleAddBed(); // Call the Add Bed function
      return;
    } else if (action === 'Delete Bed') {
      handleDeleteBed(); // Call the Delete Bed function
      return;
    } else if (action === 'Allocate Bed') {
      // Navigate to AllocateBed with state containing the selected bed info
      navigate('/bedallocation/allocate-bed', { 
        state: { 
          selectedBed: {
            ...selectedBed,
            building_name: buildingName,
            room_name: roomName,
            bed_rent: selectedBed.amount
          }
        }
      });
      return;
    } else if (action === 'View Allocation Details') {
      // Open ViewAllocationDetails in new tab with allocation_id
      if (selectedBed.allocation_id) {
        window.open(`/bedallocation/allocation/${selectedBed.allocation_id}`, '_blank');
      }
      return;
    } else if (action === 'View PG Details') {
      // Open ViewPGDetails in new tab with pg_id
      if (selectedBed.pg_id) {
        window.open(`/paying-guest/view/${selectedBed.pg_id}`, '_blank');
      }
      return;
    }
  };

  return (
    <div className="beds-container layout-more">
      {loading ? (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '300px',
          gap: '10px'
        }}>
          <RTSpinner message='Loading Beds...' />
        </div>
      ) : (
        <>
          {/* Display Room ID in a separate row */}
          {!selectedBed && (
            <div>
              <h3 style={{ textAlign: 'center' }}>Room #{roomNumber}</h3>
              <RTButton variant="secondary" onClick={() => handleMenuButtonClick('Add Bed')}>
                Add Bed
              </RTButton>
            </div>
          )}
          {selectedBed && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              <h4>
                Room #{roomNumber} - Bed #{selectedBedNumber}
              </h4>
              <div
                className="action-menu-row"
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '10px',
                }}
              >
                {selectedBed && (
                  <RTButton variant="secondary" onClick={() => handleMenuButtonClick('Add Bed')}>
                    Add Bed
                  </RTButton>
                )}
                {selectedBed && (
                  <RTButton variant="secondary" onClick={() => handleMenuButtonClick('Delete Bed')}>
                    Delete Bed
                  </RTButton>
                )}
                {selectedBed.is_vacant && (
                  <RTButton variant="secondary" onClick={() => handleMenuButtonClick('Allocate Bed')}>
                    Allocate Bed
                  </RTButton>
                )}
                {!selectedBed.is_vacant && (
                  <RTButton variant="secondary" onClick={() => handleMenuButtonClick('View PG Details')}>
                    View PG Details
                  </RTButton>
                )}
                {!selectedBed.is_vacant && selectedBed.allocation_id && (
                  <RTButton variant="secondary" onClick={() => handleMenuButtonClick('View Allocation Details')}>
                    View Allocation Details
                  </RTButton>
                )}
              </div>
            </div>
          )}

          {rows.map((row, rowIndex) => (
            <div key={rowIndex} className="bed-row">
              {row.map((bedCard) => bedCard)} {/* Render each bed card */}
            </div>
          ))}
        </>
      )}

    </div>
  );
};

export default Beds;